import type {
  CartEventOptions,
  CartItemInput,
  ConvertProductOptions,
  GoogleAnalyticsEventData,
  GoogleAnalyticsItem,
  ListInfo,
  ProductItemInput,
  SingleItemEventOptions,
  TransactionData,
  TransactionItemInput,
} from '@maestro/google-analytics/types'

import { injectable } from 'inversify'

import { createLogger } from '@maestro/core/logger'
import {
  type CartEntry,
  type EventPayloadType,
  EventType,
  type Price,
  type Product,
  StockStatusCode,
} from '@maestro/data-layer'
import { theme } from '@maestro/local/theme'

const log = createLogger('maestro:google-analytics')

const round2 = (n: number) => Math.round(n * 100) / 100

@injectable()
export class GoogleAnalyticsConverter {
  private isDebug: boolean

  constructor(isDebug = false) {
    this.isDebug = isDebug
  }

  public convertEventData<T extends EventPayloadType[EventType]>(
    eventType: EventType,
    eventData: T
  ): GoogleAnalyticsEventData {
    if (this.isDebug) {
      log.debug(
        `[GoogleAnalyticsConverter - convertEventData] Converting event: ${eventType}`,
        eventData
      )
    }

    try {
      const eventAttributes = this.getEventAttributes(eventType)

      const convertedData = this.convertEventByAttributes(
        eventType,
        eventData,
        eventAttributes
      )

      return convertedData
    } catch (error) {
      if (this.isDebug) {
        log.error(
          `[GoogleAnalyticsConverter - convertEventData] Error converting ${eventType}:`,
          error
        )
      }
      return eventData as GoogleAnalyticsEventData
    }
  }

  protected getEventAttributes(eventType: EventType | string): string[] {
    switch (eventType) {
      case EventType.SEARCH:
        return ['search_term', 'number_of_results']

      case EventType.PRODUCT_LIST_VIEWED:
        return ['items_list']

      case EventType.PRODUCT_CLICKED:
        return ['single_item_list']

      case EventType.PRODUCT_VIEWED:
        return ['single_item']

      case EventType.PRODUCT_ADDED_TO_CART:
        return ['single_item', 'atc_type']

      case EventType.PRODUCT_REMOVED_FROM_CART:
        return ['removed_items']

      case EventType.REMOVED_ALL_PRODUCTS_FROM_CART:
        return ['removed_items', 'location']

      case EventType.PRODUCT_ADDED_TO_WISHLIST:
        return ['single_item']

      case EventType.CART_VIEWED:
        return ['cart_items']

      case EventType.CHECKOUT_STARTED:
        return ['cart_items', 'order_coupon']

      case EventType.SHIPPING_INFO_ADDED:
        return ['cart_items', 'order_coupon', 'shipping_tier']

      case EventType.PAYMENT_INFO_ADDED:
        return ['cart_items', 'order_coupon', 'payment_type']

      case EventType.ORDER_COMPLETED:
        return ['transaction_items', 'transaction_data']

      case EventType.COLLECT_AT_STORE_ORDER_COMPLETED:
        return ['transaction_items', 'transaction_data']

      case EventType.TOP_NAVIGATION_CLICKED:
        return ['item_clicked']

      case EventType.MENU_CLICKED:
      case EventType.SUB_MENU_CLICKED:
        return ['hierarchical_navigation']

      case EventType.PRODUCTS_SORTED:
        return ['sort_by']

      case EventType.PRODUCTS_FILTERED:
        return ['filter_name', 'filter_by']

      case EventType.SLIDE_IN_OPENED:
        return ['slide_in_data']

      case EventType.JOINED_CLUB:
      case EventType.USER_LOGGED_OUT:
      case EventType.LEFT_CLUB:
        return ['user_id', 'user_data', 'action']

      case EventType.USER_SIGNED_UP:
      case EventType.USER_LOGGED_IN:
        return ['method', 'user_id', 'user_data']

      case EventType.STORE_SELECTED:
        return ['current_store', 'previous_store']

      case EventType.PROMOTION_SELECTED:
      case EventType.PROMOTION_VIEWED:
        return ['promotion_data']

      case EventType.FOOTER_LINK_CLICKED:
        return ['level1', 'level2', 'click_url', 'click_text']

      case EventType.SEARCH_SUGGESTIONS_CLICKED:
        return ['search_section', 'search_term', 'click_url']

      case EventType.CTA_CLICKED:
        return ['location', 'name', 'url']

      case EventType.SEARCH_ARTICLES_VIEWED:
        return ['name', 'url']

      case EventType.BREADCRUMBS_CLICKED:
        return ['position', 'click_url', 'click_text']

      case EventType.CART_SCROLL_COMPLETE:
        return ['cart_size', 'value']

      case EventType.ALERT_SHOWN:
      case EventType.HELPBOX:
        return ['alert_type', 'alert_title']

      case EventType.ERROR_MESSAGE:
        return ['error_type', 'error_title']

      case EventType.ACCORDION:
        return ['accordion_title', 'action', 'location']

      case EventType.BALL_MENU_CLICKED:
        return ['click_text']

      default:
        return ['generic']
    }
  }

  protected convertEventByAttributes<T extends EventPayloadType[EventType]>(
    eventType: EventType,
    eventData: T,
    attributes: string[]
  ): GoogleAnalyticsEventData {
    const result: GoogleAnalyticsEventData = {}

    // Cast to Record for property access since we know the runtime structure
    const data = eventData as Record<string, unknown>

    for (const attribute of attributes) {
      switch (attribute) {
        case 'search_term':
          result.search_term = data.searchTerm as string
          break

        case 'search_section':
          result.search_section = data.searchSection as string
          break

        case 'number_of_results':
          result.number_of_results = data.numberOfResults as number
          break

        case 'sort_by':
          result.sort_by = data.sortBy as string
          break

        case 'filter_name':
          result.filter_name = data.filterName as string
          break

        case 'filter_by':
          result.filter_by = data.filterValue as string
          break

        case 'items_list': {
          const items = this.getItems(data)
          Object.assign(
            result,
            this.convertListEventBase(
              items.map((item) => this.mapToProductItem(item)),
              this.getListInfo(data)
            )
          )
          break
        }

        case 'atc_type': {
          const item = this.getItem(data)
          if (result.ecommerce && item.atcType) {
            ;(result.ecommerce as Record<string, unknown>).atc_type =
              item.atcType
          }
          break
        }

        case 'single_item_list': {
          const item = this.getItem(data)
          Object.assign(
            result,
            this.convertListEventBase(
              [this.mapToProductItem(item)],
              this.getListInfo(data)
            )
          )
          break
        }

        case 'single_item': {
          const item = this.getItem(data)
          Object.assign(
            result,
            this.convertSingleItemEvent(
              this.mapToCartItem(item),
              this.getListInfo(data)
            )
          )
          break
        }

        case 'list_info': {
          if (data.listId) {
            result.item_list_id = data.listId as string
          }
          if (data.listName) {
            result.item_list_name = data.listName as string
          }
          break
        }

        case 'position': {
          result.position = data.position as string
          break
        }

        case 'cart_items': {
          const items = this.getItems(data)
          const cartOptions: CartEventOptions = {
            orderLevelCoupon: data.coupon as string | undefined,
            shippingTier: data.shippingTier as string | undefined,
            paymentType: data.paymentType as string | undefined,
            deliveryMethod: data.deliveryMethod as string | undefined,
            checkoutMethod: data.checkoutMethod as string | undefined,
            cartSize: items.length,
            currency: data.currency as string | undefined,
            value: data.value as number | undefined,
          }
          Object.assign(
            result,
            this.convertCartEventBase(
              items.map((item) => this.mapToCartItem(item)),
              cartOptions
            )
          )
          break
        }

        case 'removed_items': {
          const items = data.items ? this.getItems(data) : [this.getItem(data)]
          Object.assign(
            result,
            this.convertCartEventBase(
              items.map((item) => this.mapToCartItem(item)),
              {
                orderLevelCoupon: undefined,
                cartItems: data.cart as CartEntry[] | undefined,
              }
            )
          )
          break
        }

        case 'transaction_items': {
          const items = this.getItems(data)
          Object.assign(
            result,
            this.convertTransactionEventBase(
              items.map((item) => ({
                product: item.product as Product,
                quantity: (item.quantity as number) || 1,
                variantId: item.variantId as string | undefined,
                coupon: undefined,
                queryID: item.queryID as string,
                listId: item.listId as string | undefined,
                listName: item.listName as string | undefined,
              })),
              {
                value: data.value as number,
                tax: data.tax as number,
                shipping: data.shipping as number,
                currency: data.currency as string,
                transactionId: data.transactionId as string,
                orderLevelCoupon: data.coupon as string,
                shippingTier: data.shippingTier as string,
                paymentType: data.paymentType as string,
                additionalData: {},
                deliveryMethod: data.deliveryMethod as string,
                checkoutMethod: data.checkoutMethod as string,
                cartSize: items.length,
              }
            )
          )
          break
        }

        case 'step':
          result.step = data.step as string
          break

        case 'item_clicked':
          result.item_clicked = data.itemClicked as string
          break

        case 'hierarchical_navigation':
          result.level1 = data.level1 as string
          result.level2 = data.level2 as string
          result.level3 = data.level3 as string
          result.menu_click_text = (data.level3 ||
            data.level2 ||
            data.level1) as string
          break

        case 'slide_in_data':
          result.slide_in_type = data.slideInType
          break

        case 'generic':
          Object.assign(result, data)
          break

        case 'link_text':
          result.link_text = data.linkText as string
          break

        case 'footer_section_title':
          result.footer_section_title = data.footerSectionTitle as string
          break

        case 'location':
          result.location = data.location as string
          break

        case 'name':
          result.name = data.name as string
          break

        case 'type':
          result.type = data.type as string
          break

        case 'alert_type':
          result.alert_type = data.alertType
          break

        case 'alert_title':
          result.alert_title = data.alertTitle
          break

        case 'error_type':
          result.error_type = data.errorType
          break

        case 'error_title':
          result.error_title = data.errorTitle
          break

        case 'accordion_title':
          result.accordion_title = data.accordionTitle
          break

        case 'title':
          result.title = data.title as string
          break

        case 'action':
          result.action = data.action as string
          break

        case 'url':
          result.url = data.url as string
          break

        case 'method':
          result.method = data.method as string
          break

        case 'cart_size':
          result.cart_size = data.cartSize as string
          break

        case 'value':
          result.value = data.value as number
          break

        case 'level1':
          result.level1 = data.level1 as string
          break

        case 'level2':
          result.level2 = data.level2 as string
          break

        case 'click_url':
          result.click_url = data.click_url as string
          break

        case 'click_text':
          result.click_text = data.click_text as string
          break

        case 'user_id':
          result.user_id = data.user_id as string
          break

        case 'user_data':
          result.user_data = data.user_data as Record<string, unknown>
          break

        case 'current_store':
          result.current_store = data.currentStore as string
          break

        case 'previous_store':
          result.previous_store = data.previousStore as string
          break

        case 'promotion_data': {
          result.ecommerce = {
            creative_name: data.creative_name,
            creative_slot: data.creative_slot,
            promotion_id: data.promotion_id,
            promotion_name: data.promotion_name,
          }
          break
        }

        default:
          break
      }
    }

    return result
  }

  private convertTransactionEventBase(
    items: TransactionItemInput[],
    transactionData: TransactionData
  ): GoogleAnalyticsEventData {
    const gaItems = items.map((item, index) => {
      const gaItem = this.convertProductToGAItem(item.product, {
        quantity: item.quantity,
        variantId: item.variantId,
        index: index + 1,
        listId: item.listId,
        listName: item.listName,
      })

      gaItem.coupon = item.coupon

      return gaItem
    })

    const ecommerceData: Record<string, unknown> = {
      transaction_id: transactionData.transactionId || undefined,
      delivery_method: transactionData.deliveryMethod,
      checkout_method: transactionData.checkoutMethod,
      value: transactionData.value,
      tax: transactionData.tax,
      shipping: transactionData.shipping,
      currency: transactionData.currency,
      shipping_tier: transactionData.shippingTier || undefined,
      payment_type: transactionData.paymentType || undefined,
      items: gaItems,
      coupon: transactionData.orderLevelCoupon || undefined,
      cart_size: transactionData.cartSize,
    }

    return {
      ecommerce: ecommerceData,
    }
  }

  protected getActivePrice(price?: Price): number {
    if (!price) {
      return 0
    }

    const hasMemberPrice =
      price.memberAmount !== undefined && price.memberAmount !== price.amount
    const hasDiscountedPrice =
      price.discountedAmount !== undefined &&
      price.discountedAmount !== price.amount

    return (
      (hasMemberPrice ? price.memberAmount : undefined) ??
      (hasDiscountedPrice ? price.discountedAmount : undefined) ??
      price.amount
    )
  }

  protected getProductDisplayPrice(
    product: Product,
    variantId?: string
  ): Price {
    const variant = variantId
      ? product.variants?.find(
          (productVariant) => productVariant.id === variantId
        )
      : undefined

    return variant?.prices?.[0] ?? product.price
  }

  protected getPricingDetails(
    product: Product,
    variantId?: string
  ): {
    price: Price
    finalPrice: number
    discount: number
  } {
    const price = this.getProductDisplayPrice(product, variantId)
    const finalPrice = this.getActivePrice(price)

    return {
      price,
      finalPrice,
      discount: Math.max(0, round2(price.amount - finalPrice)),
    }
  }

  private convertCartEventBase(
    items: CartItemInput[],
    options: CartEventOptions = {}
  ): GoogleAnalyticsEventData {
    const gaItems = items.map((item, index) => {
      const gaItem = this.convertProductToGAItem(item.product, {
        quantity: item.quantity,
        variantId: item.variantId,
        index: index + 1,
        affiliation: item.affiliation,
        listId: item.listId,
        listName: item.listName,
      })

      if (options.cartItems) {
        gaItem.index =
          [...options.cartItems].findIndex(
            (cartItem) => cartItem.productId === item.product.id
          ) + 1
      }

      if (item.coupon) {
        gaItem.coupon = item.coupon
      }

      return gaItem
    })

    const totalValue =
      options.value ??
      items.reduce((sum, item) => {
        const { finalPrice } = this.getPricingDetails(
          item.product,
          item.variantId
        )
        return sum + finalPrice * item.quantity
      }, 0)

    const currency =
      options.currency ??
      items[0]?.product?.price?.currency ??
      theme().priceConfig.currencyIso

    const ecommerceData: Record<string, unknown> = {
      currency,
      value: totalValue,
      items: gaItems,
    }

    if (options.cartSize !== undefined) {
      ecommerceData.cart_size = options.cartSize
    }

    if (options.orderLevelCoupon) {
      ecommerceData.coupon = options.orderLevelCoupon
    }

    if (options.shippingTier) {
      ecommerceData.shipping_tier = options.shippingTier || undefined
    }

    if (options.paymentType) {
      ecommerceData.payment_type = options.paymentType || undefined
    }

    if (options.deliveryMethod) {
      ecommerceData.delivery_method = options.deliveryMethod
    }

    if (options.checkoutMethod) {
      ecommerceData.checkout_method = options.checkoutMethod
    }
    return {
      ecommerce: ecommerceData,

      ...options.additionalData,
    }
  }

  private convertSingleItemEvent(
    item: CartItemInput,
    options: SingleItemEventOptions = {}
  ): GoogleAnalyticsEventData {
    const gaItem = this.convertProductToGAItem(item.product, {
      quantity: item.quantity,
      variantId: item.variantId,
      index: item.position,
      listId: options.listId,
      listName: options.listName,
    })

    if (item.coupon) {
      gaItem.coupon = item.coupon
    }

    const { price, finalPrice } = this.getPricingDetails(
      item.product,
      item.variantId
    )

    const totalValue = finalPrice * item.quantity

    return {
      ecommerce: {
        currency: price.currency,
        value: totalValue,
        items: [gaItem],
      },
    }
  }

  private convertListEventBase(
    items: ProductItemInput[],
    listInfo: ListInfo
  ): GoogleAnalyticsEventData {
    const gaItems = items.map((item) => {
      const gaItem = this.convertProductToGAItem(item.product, {
        quantity: item.quantity,
        variantId: item.variantId,
        index: item.position,
        listId: listInfo.listId,
        listName: listInfo.listName,
      })

      if (item.coupon) {
        gaItem.coupon = item.coupon
      }

      return gaItem
    })

    return {
      ecommerce: {
        item_list_id: listInfo.listId,
        item_list_name: listInfo.listName,
        items: gaItems,
      },
    }
  }

  private getListInfo(eventData: Record<string, unknown>): ListInfo {
    return {
      listId: eventData.listId as string,
      listName: eventData.listName as string,
    }
  }

  private getItem(eventData: Record<string, unknown>): Record<string, unknown> {
    return eventData.item as Record<string, unknown>
  }

  private getItems(
    eventData: Record<string, unknown>
  ): Record<string, unknown>[] {
    return eventData.items as Record<string, unknown>[]
  }

  private mapToProductItem(item: Record<string, unknown>): ProductItemInput {
    return {
      product: item.product as Product,
      quantity: (item.quantity as number) || 1,
      variantId: item.variantId as string | undefined,
      position: item.position as number,
      coupon: (item.coupon as string) || undefined,
    }
  }

  private mapToCartItem(item: Record<string, unknown>): CartItemInput {
    return {
      product: item.product as Product,
      quantity: (item.quantity as number) || 1,
      variantId: item.variantId as string | undefined,
      coupon: (item.coupon as string) || undefined,
      atcType: (item.atcType as string) || undefined,
      affiliation: (item.affiliation as string) || undefined,
      listId: (item.listId as string) || undefined,
      listName: (item.listName as string) || undefined,
      position: item.position as number,
    }
  }

  public convertProductToGAItem(
    product: Product,
    options: ConvertProductOptions = {}
  ): GoogleAnalyticsItem {
    const {
      quantity = 1,
      variantId,
      index,
      listId,
      listName,
      listType,
      affiliation,
    } = options

    const variant = variantId
      ? product.variants?.find((v) => v.id === variantId)
      : null
    const { price, finalPrice, discount } = this.getPricingDetails(
      product,
      variantId
    )

    const categories = (product.categories || []).slice(0, 4)
    const categoryData: Record<string, string> = {}

    categories.forEach((category, idx) => {
      if (idx === 0) {
        categoryData.item_category = category.name
      } else {
        categoryData[`item_category${idx + 1}`] = category.name
      }
    })

    const campaignLabel = product.labels?.[0]?.label

    const campaignName = product.labels?.[0]?.name

    return {
      item_id: variant?.sku || product.sku || product.id,
      item_name: product.name,
      item_variant: variantId || product.defaultVariantId,
      item_brand: product.brand?.name,
      price: round2(finalPrice),
      currency: price.currency,
      quantity: quantity,
      affiliation: affiliation || 'online',
      ...categoryData,
      ...(index !== undefined && { index }),
      ...(listId && { item_list_id: listId }),
      ...(listName && { item_list_name: listName }),
      ...(listType && { item_list_type: listType }),
      ...(product.stock && {
        availability_online:
          product.stock.statusCode == StockStatusCode.inStock
            ? StockStatusCode.inStock
            : StockStatusCode.outOfStock,
        availability_in_stores:
          product.stock.stockAvailability == StockStatusCode.inStock
            ? StockStatusCode.inStock
            : StockStatusCode.outOfStock,
      }),
      on_sale: 'no',
      club_offer: product.labels?.some(({ cssClass }) =>
        cssClass?.includes('member')
      )
        ? 'yes'
        : 'no',
      ...(discount > 0 && {
        discount,
        on_sale: 'yes',
      }),
      item_type: product.type,
      campaign_label: campaignLabel,
      campaign_type: campaignName,
    }
  }
}
